import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../../i18n/TranslationContext";
import Footer from "../../layouts/Footer";

export default function PrivacyPolicy() {
  const { lang, toggleLang } = useTranslation();
  const [theme, setTheme] = useState(
    () => localStorage.getItem("theme") || "dark",
  );

  useEffect(() => {
    localStorage.setItem("theme", theme);
    document.body.classList.remove("theme-light", "theme-dark");
    document.body.classList.add(`theme-${theme}`);
    window.scrollTo(0, 0);
  }, [theme]);

  const toggleTheme = () =>
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));

  const isAr = lang === "ar";

  return (
    <div
      className={`aq-home theme-${theme}`}
      dir={isAr ? "rtl" : "ltr"}
      lang={lang}
    >
      <header className="aq-nav">
        <div className="aq-container aq-nav-inner">
          <Link to="/" className="aq-brand" style={{ textDecoration: "none", color: "inherit" }}>
            <div className="aq-brand-text">
              <span className="aq-brand-name">Aman-QO</span>
              <span className="aq-brand-tag">
                {isAr ? "الأمان الرقمي.. ببساطة" : "Digital Safety, Simplified"}
              </span>
            </div>
          </Link>

          <div className="aq-nav-cta">
            <button className="aq-btn aq-btn-ghost aq-theme-toggle" onClick={toggleLang}>
              {lang === "ar" ? "EN" : "ع"}
            </button>
            <button className="aq-btn aq-btn-ghost aq-theme-toggle" onClick={toggleTheme}>
              {theme === "dark"
                ? isAr ? "فاتح ☀️" : "Light ☀️"
                : isAr ? "داكن 🌙" : "Dark 🌙"}
            </button>
            <Link to="/" className="aq-btn aq-btn-solid" style={{ textDecoration: "none" }}>
              {isAr ? "العودة للرئيسية" : "Back to Home"}
            </Link>
          </div>
        </div>
      </header>

      <section className="aq-section aq-legal-page">
        <div className="aq-container">
          <div className="aq-legal-page-head">
            <span className="aq-pricing-kicker">
              {isAr ? "سياسة الخصوصية" : "Privacy Policy"}
            </span>
            <h1 className="aq-headline">
              {isAr ? "سياسة الخصوصية لمنصة Aman-QO" : "Aman-QO Privacy Policy"}
            </h1>
            <p className="aq-muted">
              {isAr
                ? "نوضح في هذه الصفحة كيف نجمع المعلومات، وكيف نستخدمها، وكيف نحمي بيانات المستخدمين عند استخدام منصة Aman-QO."
                : "This page explains how we collect information, how we use it, and how we protect user data when using Aman-QO."}
            </p>
          </div>

          <div className="aq-legal-doc">
            <section className="aq-legal-doc-block">
              <h2>{isAr ? "1. مقدمة" : "1. Introduction"}</h2>
              <p>
                {isAr
                  ? "تلتزم Aman-QO بحماية خصوصية المستخدمين وتتعامل مع البيانات بمسؤولية وشفافية. باستخدامك للمنصة أو التطبيق أو أي من خدماتنا المرتبطة بفحص الروابط و QR Codes، فإنك توافق على هذه السياسة."
                  : "Aman-QO is committed to protecting user privacy and handling data responsibly and transparently. By using the platform, app, or any of our services related to link and QR code scanning, you agree to this policy."}
              </p>
            </section>

            <section className="aq-legal-doc-block">
              <h2>{isAr ? "2. البيانات التي قد نجمعها" : "2. Information We May Collect"}</h2>
              <ul className="aq-list">
                <li>
                  {isAr
                    ? "بيانات الحساب الأساسية مثل الاسم أو البريد الإلكتروني أو رقم الهاتف عند التسجيل."
                    : "Basic account information such as name, email address, or phone number when registering."}
                </li>
                <li>
                  {isAr
                    ? "الروابط أو QR Codes التي يقوم المستخدم بإرسالها للفحص."
                    : "Links or QR codes submitted by the user for scanning."}
                </li>
                <li>
                  {isAr
                    ? "بيانات تقنية مثل نوع الجهاز، نظام التشغيل، المتصفح، وعنوان IP بصورة محدودة لتحسين الأداء والأمان."
                    : "Technical data such as device type, operating system, browser, and limited IP information for performance and security improvement."}
                </li>
                <li>
                  {isAr
                    ? "بيانات الاستخدام مثل عدد مرات الفحص، نوع الاشتراك، والخصائص المستخدمة داخل المنصة."
                    : "Usage data such as number of scans, subscription type, and features used inside the platform."}
                </li>
              </ul>
            </section>

            <section className="aq-legal-doc-block">
              <h2>{isAr ? "3. كيف نستخدم البيانات" : "3. How We Use Information"}</h2>
              <ul className="aq-list">
                <li>
                  {isAr
                    ? "لتقديم خدمة فحص الروابط و QR Codes وتحليل مستوى الخطورة."
                    : "To provide link and QR code scanning and risk analysis services."}
                </li>
                <li>
                  {isAr
                    ? "لإرسال التنبيهات والنتائج والتوعية الأمنية المناسبة."
                    : "To deliver scan results, warnings, and relevant security awareness messages."}
                </li>
                <li>
                  {isAr
                    ? "لتطوير الأداء وتحسين الدقة وتجربة المستخدم."
                    : "To improve platform performance, accuracy, and user experience."}
                </li>
                <li>
                  {isAr
                    ? "لمنع إساءة الاستخدام أو الأنشطة غير القانونية أو محاولات التحايل."
                    : "To prevent misuse, illegal activities, or fraudulent behavior."}
                </li>
              </ul>
            </section>

            <section className="aq-legal-doc-block">
              <h2>{isAr ? "4. مشاركة البيانات" : "4. Data Sharing"}</h2>
              <p>
                {isAr
                  ? "لا تقوم Aman-QO ببيع البيانات الشخصية للمستخدمين. وقد يتم مشاركة بعض البيانات بشكل محدود مع مزودي خدمات تقنيين أو شركاء تشغيل عند الحاجة لتقديم الخدمة أو تحسينها أو الامتثال للمتطلبات القانونية."
                  : "Aman-QO does not sell users’ personal data. Some limited information may be shared with technical service providers or operating partners when necessary to deliver the service, improve it, or comply with legal obligations."}
              </p>
            </section>

            <section className="aq-legal-doc-block">
              <h2>{isAr ? "5. حماية البيانات" : "5. Data Protection"}</h2>
              <p>
                {isAr
                  ? "نتخذ إجراءات تقنية وتنظيمية مناسبة لحماية البيانات من الوصول غير المصرح به أو التعديل أو الفقد أو الإساءة. ومع ذلك، لا توجد وسيلة نقل أو تخزين إلكتروني تضمن الحماية بنسبة 100%."
                  : "We apply appropriate technical and organizational measures to protect data against unauthorized access, alteration, loss, or misuse. However, no electronic transmission or storage method can guarantee absolute security."}
              </p>
            </section>

            <section className="aq-legal-doc-block">
              <h2>{isAr ? "6. الاحتفاظ بالبيانات" : "6. Data Retention"}</h2>
              <p>
                {isAr
                  ? "نحتفظ بالبيانات فقط للمدة اللازمة لتقديم الخدمة، وتحسينها، والامتثال للالتزامات القانونية أو التنظيمية، ثم يتم حذفها أو تقليل إمكانية ربطها بالمستخدم عند عدم الحاجة إليها."
                  : "We retain data only for as long as needed to provide the service, improve it, and meet legal or regulatory obligations. After that, it is deleted or de-identified where appropriate."}
              </p>
            </section>

            <section className="aq-legal-doc-block">
              <h2>{isAr ? "7. حقوق المستخدم" : "7. User Rights"}</h2>
              <ul className="aq-list">
                <li>
                  {isAr
                    ? "طلب مراجعة أو تحديث بعض بيانات الحساب."
                    : "Request review or update of certain account information."}
                </li>
                <li>
                  {isAr
                    ? "طلب حذف الحساب أو إيقاف استخدامه وفقًا لما تسمح به الخدمة والالتزامات القانونية."
                    : "Request account deletion or deactivation, subject to service and legal requirements."}
                </li>
                <li>
                  {isAr
                    ? "الاستفسار عن كيفية التعامل مع البيانات من خلال وسائل التواصل الرسمية."
                    : "Inquire about how data is handled through official contact channels."}
                </li>
              </ul>
            </section>

            <section className="aq-legal-doc-block">
              <h2>{isAr ? "8. خصوصية الأطفال" : "8. Children’s Privacy"}</h2>
              <p>
                {isAr
                  ? "الخدمة غير موجهة للأطفال دون السن القانوني المطبق في بلد الاستخدام، ولا نجمع عن قصد بيانات شخصية منهم دون موافقة مناسبة من ولي الأمر عند الحاجة."
                  : "The service is not intended for children below the legally applicable age in the user’s country, and we do not knowingly collect personal information from them without appropriate parental consent where required."}
              </p>
            </section>

            <section className="aq-legal-doc-block">
              <h2>{isAr ? "9. التعديلات على السياسة" : "9. Changes to This Policy"}</h2>
              <p>
                {isAr
                  ? "قد تقوم Aman-QO بتحديث سياسة الخصوصية من وقت لآخر. ويعد استمرارك في استخدام المنصة بعد نشر التعديلات موافقة على النسخة المحدثة."
                  : "Aman-QO may update this Privacy Policy from time to time. Continued use of the platform after updates are posted constitutes acceptance of the revised version."}
              </p>
            </section>

            <section className="aq-legal-doc-block">
              <h2>{isAr ? "10. التواصل" : "10. Contact"}</h2>
              <p>
                {isAr
                  ? "لأي استفسارات تتعلق بالخصوصية أو البيانات، يمكن التواصل عبر البريد الإلكتروني:"
                  : "For any privacy or data-related questions, you may contact us by email:"}
              </p>
              <p>
                <b>amanqo.hq@gmail.com</b>
              </p>
            </section>
          </div>
        </div>
      </section>

      <Footer
        lang={lang}
        t={(key) => {
          const map = {
            "footer.rights": isAr ? "جميع الحقوق محفوظة." : "All rights reserved.",
            "nav.about": isAr ? "عن الشركة" : "About",
          };
          return map[key] || key;
        }}
      />
    </div>
  );
}